import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/header";
import LiveList from "../../components/live/LiveList";
import LiveHost from "../../components/live/LiveHost";
import LiveViewer from "../../components/live/LiveViewer";
import { liveReducer, LIVE_ACTIONS } from "../../reducers/liveReducer";
import {
  createLive,
  getActiveLives,
  getLive,
  endLive as endLiveApi,
  uploadRecording,
} from "../../functions/live";
import "./style.css";

const initialState = {
  activeLives: [],
  currentLive: null,
  isHost: false,
  viewerCount: 0,
  chat: [],
  reactions: [],
  loading: false,
  error: null,
  stream: null,
  isRecording: false,
};

export default function Live({ socket }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => ({ ...state }));

  const [state, dispatch] = useReducer(liveReducer, initialState);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    privacy: "friends",
    chatEnabled: true,
    reactionsEnabled: true,
    recordingEnabled: false,
  });
  const [creating, setCreating] = useState(false);

  const currentLiveIdRef = useRef(null);

  // Carregar lives ativas
  const loadActiveLives = useCallback(async () => {
    dispatch({ type: LIVE_ACTIONS.SET_LOADING, payload: true });
    const result = await getActiveLives(user.token);
    if (result.status === "ok") {
      dispatch({ type: LIVE_ACTIONS.SET_ACTIVE_LIVES, payload: result.data });
    } else {
      dispatch({ type: LIVE_ACTIONS.SET_ERROR, payload: result.message });
    }
  }, [user.token]);

  // Carregar live específica (via URL)
  useEffect(() => {
    const liveId = searchParams.get("id");
    const mode = searchParams.get("mode");

    if (liveId) {
      loadLiveById(liveId, mode === "host");
    } else {
      loadActiveLives();
    }
  }, [searchParams, loadActiveLives]);

  // Carregar live por ID
  const loadLiveById = async (liveId, asHost = false) => {
    dispatch({ type: LIVE_ACTIONS.SET_LOADING, payload: true });
    const result = await getLive(liveId, user.token);

    if (result.status === "ok") {
      dispatch({ type: LIVE_ACTIONS.SET_CURRENT_LIVE, payload: result.data });
      dispatch({ type: LIVE_ACTIONS.SET_IS_HOST, payload: asHost });
      dispatch({
        type: LIVE_ACTIONS.SET_VIEWER_COUNT,
        payload: result.data.currentViewers || 0,
      });
      currentLiveIdRef.current = liveId;
    } else {
      dispatch({ type: LIVE_ACTIONS.SET_ERROR, payload: result.message });
    }
  };

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    // Nova live iniciada por amigo
    const handleLiveStarted = (data) => {
      dispatch({ type: LIVE_ACTIONS.ADD_ACTIVE_LIVE, payload: data });
    };

    // Live encerrada
    const handleLiveEnded = ({ liveId }) => {
      dispatch({ type: LIVE_ACTIONS.REMOVE_ACTIVE_LIVE, payload: liveId });

      if (currentLiveIdRef.current === liveId) {
        dispatch({ type: LIVE_ACTIONS.CLEAR_CURRENT_LIVE });
        currentLiveIdRef.current = null;
      }
    };

    // Viewer entrou
    const handleViewerJoined = ({ liveId, user: viewer, viewerCount }) => {
      if (currentLiveIdRef.current === liveId) {
        dispatch({ type: LIVE_ACTIONS.SET_VIEWER_COUNT, payload: viewerCount });
      }
    };

    // Viewer saiu
    const handleViewerLeft = ({ liveId, userId, viewerCount }) => {
      if (currentLiveIdRef.current === liveId) {
        dispatch({ type: LIVE_ACTIONS.SET_VIEWER_COUNT, payload: viewerCount });
      }
    };

    // Mensagem de chat
    const handleChatMessage = ({ liveId, user: sender, message, sentAt }) => {
      if (currentLiveIdRef.current === liveId) {
        dispatch({
          type: LIVE_ACTIONS.ADD_CHAT_MESSAGE,
          payload: { user: sender, message, sentAt },
        });
      }
    };

    // Reação recebida
    const handleReaction = ({ liveId, userId, reaction, user: reacter }) => {
      if (currentLiveIdRef.current === liveId) {
        dispatch({
          type: LIVE_ACTIONS.ADD_REACTION,
          payload: { reaction, user: reacter },
        });
      }
    };

    socket.on("live:started", handleLiveStarted);
    socket.on("live:ended", handleLiveEnded);
    socket.on("live:viewer_joined", handleViewerJoined);
    socket.on("live:viewer_left", handleViewerLeft);
    socket.on("live:chat_message", handleChatMessage);
    socket.on("live:reaction_received", handleReaction);

    return () => {
      socket.off("live:started", handleLiveStarted);
      socket.off("live:ended", handleLiveEnded);
      socket.off("live:viewer_joined", handleViewerJoined);
      socket.off("live:viewer_left", handleViewerLeft);
      socket.off("live:chat_message", handleChatMessage);
      socket.off("live:reaction_received", handleReaction);
    };
  }, [socket]);

  // Limpar reações antigas periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: LIVE_ACTIONS.CLEAR_REACTIONS });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Criar nova live
  const handleCreateLive = async () => {
    setCreating(true);
    const result = await createLive(
      {
        title: createForm.title || `Live de ${user.first_name}`,
        description: createForm.description,
        privacy: createForm.privacy,
        settings: {
          chatEnabled: createForm.chatEnabled,
          reactionsEnabled: createForm.reactionsEnabled,
        },
        recording: {
          enabled: createForm.recordingEnabled,
        },
      },
      user.token
    );

    if (result.status === "ok") {
      setShowCreateModal(false);
      dispatch({ type: LIVE_ACTIONS.SET_CURRENT_LIVE, payload: result.data });
      dispatch({ type: LIVE_ACTIONS.SET_IS_HOST, payload: true });
      currentLiveIdRef.current = result.data._id;
      navigate(`/live?id=${result.data._id}&mode=host`);
    } else {
      alert(result.message);
    }
    setCreating(false);
  };

  // Entrar em uma live como viewer
  const handleJoinLive = (live) => {
    dispatch({ type: LIVE_ACTIONS.SET_CURRENT_LIVE, payload: live });
    dispatch({ type: LIVE_ACTIONS.SET_IS_HOST, payload: false });
    dispatch({
      type: LIVE_ACTIONS.SET_VIEWER_COUNT,
      payload: live.currentViewers || 0,
    });
    currentLiveIdRef.current = live._id;
    navigate(`/live?id=${live._id}`);
  };

  // Sair da live
  const handleLeaveLive = () => {
    dispatch({ type: LIVE_ACTIONS.CLEAR_CURRENT_LIVE });
    currentLiveIdRef.current = null;
    navigate("/live");
    loadActiveLives();
  };

  // Encerrar live (host)
  const handleEndLive = async (recordingBlob) => {
    const liveId = currentLiveIdRef.current;

    // Encerrar a live
    await endLiveApi(liveId, user.token);

    // Upload da gravação se existir
    if (recordingBlob && createForm.recordingEnabled) {
      await uploadRecording(liveId, recordingBlob, user.token, (percent) => {
        console.log(`Upload: ${percent}%`);
      });
    }

    dispatch({ type: LIVE_ACTIONS.CLEAR_CURRENT_LIVE });
    currentLiveIdRef.current = null;
    navigate("/live");
    loadActiveLives();
  };

  // Enviar mensagem no chat
  const handleChatMessage = (message) => {
    if (!socket || !currentLiveIdRef.current) return;
    socket.emit("live:chat", {
      liveId: currentLiveIdRef.current,
      message,
    });
  };

  // Enviar reação
  const handleReaction = (reaction) => {
    if (!socket || !currentLiveIdRef.current) return;
    socket.emit("live:reaction", {
      liveId: currentLiveIdRef.current,
      reaction,
    });
  };

  // Renderizar conteúdo
  const renderContent = () => {
    // Se está em uma live
    if (state.currentLive) {
      // Como host
      if (state.isHost) {
        return (
          <LiveHost
            liveId={state.currentLive._id}
            user={user}
            socket={socket}
            viewerCount={state.viewerCount}
            chat={state.chat}
            reactions={state.reactions}
            settings={state.currentLive.settings}
            recordingEnabled={state.currentLive.recording?.enabled}
            onEnd={handleEndLive}
            onChatMessage={handleChatMessage}
            onReaction={handleReaction}
          />
        );
      }

      // Como viewer
      return (
        <LiveViewer
          liveId={state.currentLive._id}
          host={state.currentLive.host}
          user={user}
          socket={socket}
          viewerCount={state.viewerCount}
          chat={state.chat}
          reactions={state.reactions}
          settings={state.currentLive.settings}
          onLeave={handleLeaveLive}
          onChatMessage={handleChatMessage}
          onReaction={handleReaction}
        />
      );
    }

    // Lista de lives
    return (
      <div className="live_page">
        <div className="live_page_header">
          <h1>Lives</h1>
          <button
            className="start_live_btn"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fa fa-video-camera"></i>
            Iniciar Live
          </button>
        </div>
        <div className="live_page_content">
          <LiveList
            lives={state.activeLives}
            onJoinLive={handleJoinLive}
            loading={state.loading}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {!state.currentLive && <Header page="live" />}
      {renderContent()}

      {/* Modal de criar live */}
      {showCreateModal && (
        <div
          className="create_live_modal_overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="create_live_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create_live_header">
              <h2>Iniciar Live</h2>
              <button
                className="close_modal_btn"
                onClick={() => setShowCreateModal(false)}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>

            <div className="create_live_body">
              <div className="create_live_field">
                <label>Título (opcional)</label>
                <input
                  type="text"
                  placeholder="Ex: Conversando com vocês..."
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, title: e.target.value })
                  }
                />
              </div>

              <div className="create_live_field">
                <label>Descrição (opcional)</label>
                <textarea
                  placeholder="Do que é essa live?"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, description: e.target.value })
                  }
                />
              </div>

              <div className="create_live_field">
                <label>Quem pode ver?</label>
                <select
                  value={createForm.privacy}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, privacy: e.target.value })
                  }
                >
                  <option value="public">Público</option>
                  <option value="friends">Apenas amigos</option>
                  <option value="followers">Seguidores</option>
                </select>
              </div>

              <div className="create_live_field">
                <label>Opções</label>
                <div className="create_live_options">
                  <label className="create_live_option">
                    <input
                      type="checkbox"
                      checked={createForm.chatEnabled}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          chatEnabled: e.target.checked,
                        })
                      }
                    />
                    <span>Permitir chat ao vivo</span>
                  </label>

                  <label className="create_live_option">
                    <input
                      type="checkbox"
                      checked={createForm.reactionsEnabled}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          reactionsEnabled: e.target.checked,
                        })
                      }
                    />
                    <span>Permitir reações</span>
                  </label>

                  <label className="create_live_option">
                    <input
                      type="checkbox"
                      checked={createForm.recordingEnabled}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          recordingEnabled: e.target.checked,
                        })
                      }
                    />
                    <span>Gravar a live</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="create_live_footer">
              <button
                className="go_live_btn"
                onClick={handleCreateLive}
                disabled={creating}
              >
                {creating ? (
                  <>
                    <div className="spinner small"></div>
                    Preparando...
                  </>
                ) : (
                  <>
                    <i className="fa fa-video-camera"></i>
                    Entrar ao Vivo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
